/* kubota-collections-slider — carousel + left-column lifestyle-image parallax.
   GSAP + ScrollTrigger are loaded GLOBALLY in theme.liquid; do NOT re-import.
   The parallax transform (scale + yPercent) lives on the IMG child
   (.kubota-collections-slider__lifestyle-img); the slide crossfade opacity lives
   on the slide parent — independent stacks, no conflict. The 1.32 scale gives
   16% overscan each side > the 14% drift, so edges stay covered inside the
   overflow:hidden lifestyle frame. */

class KubotaCollectionsSlider extends HTMLElement {
  connectedCallback() {
    // Parallax runs FIRST, before the <2-slide carousel guard, so single-slide
    // instances still parallax (carousel short-circuits, parallax does not).
    this._setupParallax();
    this.slideCount = parseInt(this.dataset.slideCount, 10) || 0;
    if (this.slideCount < 2) return;

    this.activeIndex = 0;
    this.cardStack = this.querySelector('.kubota-collections-slider__card-stack');
    this.lifestyleStack = this.querySelector('.kubota-collections-slider__lifestyle');
    this.dots = Array.from(this.querySelectorAll('.kubota-collections-slider__dot'));
    this.arrows = Array.from(this.querySelectorAll('.kubota-collections-slider__arrow'));

    this._mobileMql = window.matchMedia('(max-width: 767.98px)');
    this._bindEvents();
  }

  disconnectedCallback() {
    if (this._pResizeHandler) window.removeEventListener('resize', this._pResizeHandler);
    this._teardownParallax?.();
    // Guard: a <2-slide instance never assigns this.arrows/dots/cardStack.
    if (this.arrows) this._unbindEvents();
  }

  _bindEvents() {
    this.arrows.forEach((a) => a.addEventListener('click', this._onArrow));
    this.dots.forEach((d) => d.addEventListener('click', this._onDot));
    this.cardStack.addEventListener('scroll', this._onCardStackScroll, { passive: true });
  }

  _unbindEvents() {
    this.arrows.forEach((a) => a.removeEventListener('click', this._onArrow));
    this.dots.forEach((d) => d.removeEventListener('click', this._onDot));
    this.cardStack.removeEventListener('scroll', this._onCardStackScroll);
  }

  _isMobile() {
    return this._mobileMql.matches;
  }

  _onArrow = (e) => {
    const dir = e.currentTarget.dataset.dir;
    this.go(dir === 'next' ? this.activeIndex + 1 : this.activeIndex - 1);
  };

  _onDot = (e) => {
    this.go(parseInt(e.currentTarget.dataset.index, 10));
  };

  // Mobile: read scroll position to sync active index when user swipes the card stack.
  _onCardStackScroll = () => {
    if (!this._isMobile()) return;
    if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
    this._scrollRaf = requestAnimationFrame(() => {
      const cardW = this.cardStack.offsetWidth;
      if (cardW === 0) return;
      const idx = Math.round(this.cardStack.scrollLeft / cardW);
      if (idx !== this.activeIndex && idx >= 0 && idx < this.slideCount) {
        this._setActive(this.lifestyleStack, idx);
        this._setActiveDot(idx);
        this.activeIndex = idx;
        this.dataset.activeIndex = String(idx);
      }
    });
  };

  go(index) {
    const mobile = this._isMobile();
    // Mobile: no wrap-around (scroll-snap stops at edges). Desktop: wrap.
    const next = mobile
      ? Math.max(0, Math.min(this.slideCount - 1, index))
      : ((index % this.slideCount) + this.slideCount) % this.slideCount;
    if (next === this.activeIndex && !mobile) return;
    this._setActive(this.cardStack, next);
    this._setActive(this.lifestyleStack, next);
    this._setActiveDot(next);
    this.activeIndex = next;
    this.dataset.activeIndex = String(next);
    if (mobile) {
      this.cardStack.scrollTo({ left: next * this.cardStack.offsetWidth, behavior: 'smooth' });
    }
  }

  _setActive(container, index) {
    if (!container) return;
    container.querySelectorAll('[data-index]').forEach((el) => {
      if (parseInt(el.dataset.index, 10) === index) {
        el.setAttribute('data-active', '');
      } else {
        el.removeAttribute('data-active');
      }
    });
  }

  _setActiveDot(index) {
    this.dots.forEach((d) => {
      if (parseInt(d.dataset.index, 10) === index) {
        d.setAttribute('data-active', '');
      } else {
        d.removeAttribute('data-active');
      }
    });
  }

  // ---- Left-column lifestyle-image parallax (GSAP global; do NOT re-import) ----
  // Ports ONLY the nested-IMAGE tween from kubota-featured-stories (scale 1.32 +
  // yPercent ±14, scrub-linked). The card-level y drift is intentionally NOT ported.

  _setupParallax() {
    this._pTriggers = [];
    this._pTimelines = [];
    this._pInit = false;
    this._pResizeRaf = null;
    this._pResizeHandler = () => {
      if (this._pResizeRaf) cancelAnimationFrame(this._pResizeRaf);
      this._pResizeRaf = requestAnimationFrame(() => this._onParallaxResize());
    };
    window.addEventListener('resize', this._pResizeHandler);
    this._tryInitParallax();
  }

  _shouldParallax() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    if (window.matchMedia('(max-width: 767.98px)').matches) return false;
    return true;
  }

  _tryInitParallax() {
    if (!this._shouldParallax()) return;
    if (window.gsap && window.ScrollTrigger) {
      this._initParallax();
    } else {
      // GSAP defines AFTER content_for_layout — wait for window load if it is not ready yet.
      window.addEventListener(
        'load',
        () => {
          if (window.gsap && window.ScrollTrigger && !this._pInit && this._shouldParallax()) {
            this._initParallax();
          }
        },
        { once: true }
      );
    }
  }

  _initParallax() {
    if (this._pInit) return;
    this._pInit = true;
    const imgs = this.querySelectorAll('.kubota-collections-slider__lifestyle-img');
    if (imgs.length === 0) return;
    const { gsap } = window;
    imgs.forEach((img) => {
      // 1.32 scale = 16% overscan each side > the ±14% drift, so the IMG stays
      // edge-to-edge inside its overflow:hidden frame throughout the scroll.
      gsap.set(img, { scale: 1.32 });
      const tween = gsap.fromTo(
        img,
        { yPercent: -14 },
        {
          yPercent: 14,
          ease: 'none',
          scrollTrigger: {
            trigger: this,
            start: 'top bottom',
            end: () => `+=${window.innerHeight + this.offsetHeight}`,
            scrub: 1,
            invalidateOnRefresh: true,
          },
        }
      );
      this._pTimelines.push(tween);
      if (tween.scrollTrigger) this._pTriggers.push(tween.scrollTrigger);
    });
  }

  _onParallaxResize() {
    const enabled = this._shouldParallax();
    if (enabled && !this._pInit) {
      this._tryInitParallax();
    } else if (!enabled && this._pInit) {
      this._teardownParallax();
      // Reset GSAP-applied transforms so images return to natural position.
      this.querySelectorAll('.kubota-collections-slider__lifestyle-img').forEach((img) => {
        img.style.transform = '';
      });
    }
  }

  _teardownParallax() {
    this._pTriggers.forEach((t) => t.kill());
    this._pTimelines.forEach((tl) => tl.kill());
    this._pTriggers = [];
    this._pTimelines = [];
    this._pInit = false;
  }
}

if (!customElements.get('kubota-collections-slider')) {
  customElements.define('kubota-collections-slider', KubotaCollectionsSlider);
}
